import React, {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { LovOption, UpdateLovOptionFields } from "../api/contracts";
import {
  ActionsColumnHeader,
  ContentSection,
  DisplayValue,
  PortalDataTable,
  PortalTableColumn,
  RecordDetailCard,
  RecordFieldRow,
  StatusPill,
} from "../components/DataDisplay";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import { RowActionMenu } from "../components/HomeworkControls";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { SingleSelectPillField } from "../components/SingleSelectPillField";
import {
  formatLovOrderingMode,
  formatLovSelectionType,
  sortLovOptions,
} from "../config/lov";
import {
  getActivityTypePillToken,
  getConfiguredPillToken,
  pillPaletteTokens,
} from "../config/palette";
import { usePortalData } from "../hooks/usePortalData";
import { usePortalMutation } from "../hooks/usePortalMutation";

interface LovOptionFormErrors {
  key?: string;
  value?: string;
  name?: string;
  colorKey?: string;
  sortOrder?: string;
  form?: string;
}

type LovOptionFormMode =
  | { kind: "create"; sortOrder: number }
  | { kind: "edit"; option: LovOption };

const colorOptions = pillPaletteTokens.map((token) => ({
  id: token,
  value: token,
  label: token,
  colorKey: token,
}));

function mutationErrorMessage(action: "save" | "status"): string {
  return action === "save"
    ? "Value could not be saved. Review the fields and try again."
    : "Value status could not be updated. Try again.";
}

function LovOptionRowActions({
  clinicianCode,
  catalogKey,
  option,
  onEdit,
  onSaved,
}: {
  clinicianCode: string;
  catalogKey: string;
  option: LovOption;
  onEdit: (option: LovOption) => void;
  onSaved: (option: LovOption) => void;
}) {
  const mutation = usePortalMutation();
  const isPending = mutation.state.status === "pending";
  const actionLabel = option.enabled ? "Disable" : "Enable";

  const handleEnabledChange = async () => {
    if (isPending) return;
    mutation.reset();
    const result = await mutation.run(`set-enabled-${option.id}`, (signal) =>
      clinicianPortalApi.setLovOptionEnabled(
        clinicianCode,
        catalogKey,
        option.id,
        !option.enabled,
        signal,
      ),
    );
    if (result?.updated) onSaved(result.option);
  };

  return (
    <div className="cp-lov-row-actions">
      <RowActionMenu
        clinicianCode={clinicianCode}
        activityName={option.name}
        canEdit={!isPending}
        onEdit={() => onEdit(option)}
        showDuplicate={false}
        canDelete={!isPending}
        isDeleting={isPending}
        onDelete={handleEnabledChange}
        deleteLabel={actionLabel}
        deletingLabel={option.enabled ? "Disabling…" : "Enabling…"}
      />
      {mutation.state.status === "error" ? (
        <p className="cp-lov-row-error" role="alert">
          {mutationErrorMessage("status")}
        </p>
      ) : null}
    </div>
  );
}

function LovOptionForm({
  clinicianCode,
  catalogKey,
  selectionType,
  mode,
  onCancel,
  onSaved,
}: {
  clinicianCode: string;
  catalogKey: string;
  selectionType: string;
  mode: LovOptionFormMode;
  onCancel: () => void;
  onSaved: (option: LovOption, shouldRefresh: boolean) => void;
}) {
  const option = mode.kind === "edit" ? mode.option : undefined;
  const isActivityType = catalogKey === "activity_types";
  const isBadge = catalogKey === "badges";
  const mutation = usePortalMutation();
  const [key, setKey] = useState(option?.key ?? "");
  const [value, setValue] = useState(option?.value ?? "");
  const [name, setName] = useState(option?.name ?? "");
  const [colorKey, setColorKey] = useState(option?.colorKey || "neutral");
  const [sortOrder, setSortOrder] = useState(
    String(option?.sortOrder ?? (mode.kind === "create" ? mode.sortOrder : 0)),
  );
  const [enabled, setEnabled] = useState(true);
  const [isDefault, setIsDefault] = useState(option?.isDefault ?? false);
  const [collection, setCollection] = useState(option?.collection ?? "");
  const [assetPath, setAssetPath] = useState(option?.assetPath ?? "");
  const [validationErrors, setValidationErrors] =
    useState<LovOptionFormErrors>({});
  const isSubmitting = mutation.state.status === "pending";
  const heading = mode.kind === "create" ? "Add Value" : "Edit Value";
  const headingId = `lov-option-${mode.kind}-heading`;
  const defaultDisabled =
    mode.kind === "create" ? !enabled : !mode.option.enabled;
  const defaultHelp =
    selectionType === "single"
      ? "Only one enabled option can be the default."
      : "Multiple options can be defaults.";

  const clearErrors = () => {
    setValidationErrors({});
    mutation.reset();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const normalizedKey = key.trim();
    const normalizedValue = value.trim();
    const normalizedName = name.trim();
    const parsedSortOrder = Number(sortOrder);
    const nextErrors: LovOptionFormErrors = {};

    if (!normalizedKey) nextErrors.key = "Key is required.";
    if (mode.kind === "create" && !isActivityType && !normalizedValue) {
      nextErrors.value = "Value is required.";
    }
    if (!normalizedName) nextErrors.name = "Name is required.";
    if (!colorKey) nextErrors.colorKey = "Color is required.";
    if (
      !sortOrder.trim() ||
      !Number.isInteger(parsedSortOrder) ||
      parsedSortOrder < 0
    ) {
      nextErrors.sortOrder = "Sort Order must be a non-negative integer.";
    }

    setValidationErrors(nextErrors);
    mutation.reset();
    if (Object.keys(nextErrors).length) return;

    const normalizedCollection = collection.trim();
    const normalizedAssetPath = assetPath.trim();
    const updateFields: UpdateLovOptionFields = {};

    if (mode.kind === "edit") {
      if (normalizedName !== mode.option.name) {
        updateFields.name = normalizedName;
      }
      if (colorKey !== mode.option.colorKey) {
        updateFields.colorKey = colorKey;
      }
      if (parsedSortOrder !== mode.option.sortOrder) {
        updateFields.sortOrder = parsedSortOrder;
      }
      if (isDefault !== mode.option.isDefault) {
        updateFields.isDefault = isDefault;
      }
      if (isBadge) {
        if (normalizedCollection !== (mode.option.collection ?? "")) {
          updateFields.collection = normalizedCollection;
        }
        if (normalizedAssetPath !== (mode.option.assetPath ?? "")) {
          updateFields.assetPath = normalizedAssetPath;
        }
      }

      if (!Object.keys(updateFields).length) {
        setValidationErrors({ form: "Make at least one change before saving." });
        return;
      }
    }

    const result =
      mode.kind === "create"
        ? await mutation.run("create-lov-option", (signal) =>
            clinicianPortalApi.createLovOption(
              clinicianCode,
              catalogKey,
              {
                key: normalizedKey,
                ...(isActivityType ? {} : { value: normalizedValue }),
                name: normalizedName,
                colorKey,
                sortOrder: parsedSortOrder,
                enabled,
                isDefault: enabled ? isDefault : false,
                ...(isBadge
                  ? {
                      collection: normalizedCollection,
                      assetPath: normalizedAssetPath,
                    }
                  : {}),
              },
              signal,
            ),
          )
        : await mutation.run("update-lov-option", (signal) =>
            clinicianPortalApi.updateLovOption(
              clinicianCode,
              catalogKey,
              mode.option.id,
              updateFields,
              signal,
            ),
          );

    if (result && ("created" in result || "updated" in result)) {
      const shouldRefresh =
        selectionType === "single" &&
        result.option.isDefault &&
        (mode.kind === "create" || updateFields.isDefault === true);
      onSaved(result.option, shouldRefresh);
    }
  };

  return (
    <section className="cp-record-form" aria-labelledby={headingId}>
      <div className="cp-record-form__header">
        <h2 id={headingId}>{heading}</h2>
        <p>
          {mode.kind === "create"
            ? "Create a value for this catalog."
            : "Update display fields. Key and Value cannot be changed."}
        </p>
      </div>
      <form onSubmit={handleSubmit} noValidate>
        <div className="cp-form-number-grid cp-lov-form-grid">
          <div className="cp-form-field">
            <label htmlFor="lov-option-key">
              Key <span aria-hidden="true">*</span>
            </label>
            <input
              id="lov-option-key"
              className="cp-form-input"
              type="text"
              value={key}
              readOnly={mode.kind === "edit"}
              aria-invalid={Boolean(validationErrors.key)}
              aria-describedby={validationErrors.key ? "lov-key-error" : undefined}
              onChange={(event) => {
                clearErrors();
                setKey(event.target.value);
              }}
            />
            {validationErrors.key ? (
              <p className="cp-form-error" id="lov-key-error">
                {validationErrors.key}
              </p>
            ) : null}
          </div>

          {mode.kind === "edit" || !isActivityType ? (
            <div className="cp-form-field">
              <label htmlFor="lov-option-value">
                Value <span aria-hidden="true">*</span>
              </label>
              <input
                id="lov-option-value"
                className="cp-form-input"
                type="text"
                value={value}
                readOnly={mode.kind === "edit"}
                aria-invalid={Boolean(validationErrors.value)}
                aria-describedby={
                  validationErrors.value ? "lov-value-error" : undefined
                }
                onChange={(event) => {
                  clearErrors();
                  setValue(event.target.value);
                }}
              />
              {validationErrors.value ? (
                <p className="cp-form-error" id="lov-value-error">
                  {validationErrors.value}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="cp-form-field">
            <label htmlFor="lov-option-name">
              Name <span aria-hidden="true">*</span>
            </label>
            <input
              id="lov-option-name"
              className="cp-form-input"
              type="text"
              value={name}
              aria-invalid={Boolean(validationErrors.name)}
              aria-describedby={validationErrors.name ? "lov-name-error" : undefined}
              onChange={(event) => {
                clearErrors();
                setName(event.target.value);
              }}
            />
            {validationErrors.name ? (
              <p className="cp-form-error" id="lov-name-error">
                {validationErrors.name}
              </p>
            ) : null}
          </div>

          <div className="cp-form-field">
            <label htmlFor="lov-option-color">
              Color <span aria-hidden="true">*</span>
            </label>
            <SingleSelectPillField
              id="lov-option-color"
              value={colorKey}
              options={colorOptions}
              onChange={(nextColorKey) => {
                clearErrors();
                setColorKey(nextColorKey);
              }}
              ariaLabel="Color"
              placeholder="Select a color"
              searchPlaceholder="Search colors"
              emptyMessage="No colors available"
              resolveColor={(option) =>
                getConfiguredPillToken(option.colorKey)
              }
              disabled={isSubmitting}
              invalid={Boolean(validationErrors.colorKey)}
            />
          </div>

          <div className="cp-form-field">
            <label htmlFor="lov-option-sort-order">
              Sort Order <span aria-hidden="true">*</span>
            </label>
            <input
              id="lov-option-sort-order"
              className="cp-form-input"
              type="number"
              min="0"
              step="1"
              value={sortOrder}
              aria-invalid={Boolean(validationErrors.sortOrder)}
              aria-describedby={
                validationErrors.sortOrder ? "lov-sort-order-error" : undefined
              }
              onChange={(event) => {
                clearErrors();
                setSortOrder(event.target.value);
              }}
            />
            {validationErrors.sortOrder ? (
              <p className="cp-form-error" id="lov-sort-order-error">
                {validationErrors.sortOrder}
              </p>
            ) : null}
          </div>

          {mode.kind === "create" ? (
            <div className="cp-form-field">
              <span className="cp-lov-checkbox-label">Status</span>
              <label className="cp-form-checkbox" htmlFor="lov-option-enabled">
                <input
                  id="lov-option-enabled"
                  type="checkbox"
                  checked={enabled}
                  onChange={(event) => {
                    clearErrors();
                    const nextEnabled = event.target.checked;
                    setEnabled(nextEnabled);
                    if (!nextEnabled) setIsDefault(false);
                  }}
                />
                Enabled
              </label>
            </div>
          ) : null}

          <div className="cp-form-field">
            <span className="cp-lov-checkbox-label">Default setting</span>
            <label className="cp-form-checkbox" htmlFor="lov-option-default">
              <input
                id="lov-option-default"
                type="checkbox"
                checked={isDefault}
                disabled={defaultDisabled}
                onChange={(event) => {
                  clearErrors();
                  setIsDefault(event.target.checked);
                }}
              />
              Default
            </label>
            <small className="cp-lov-default-help">
              {defaultDisabled
                ? "Enable this option before making it a default."
                : defaultHelp}
            </small>
          </div>

          {isBadge ? (
            <>
              <div className="cp-form-field">
                <label htmlFor="lov-option-collection">Collection</label>
                <input
                  id="lov-option-collection"
                  className="cp-form-input"
                  type="text"
                  value={collection}
                  onChange={(event) => {
                    clearErrors();
                    setCollection(event.target.value);
                  }}
                />
              </div>
              <div className="cp-form-field">
                <label htmlFor="lov-option-asset-path">Asset Path</label>
                <input
                  id="lov-option-asset-path"
                  className="cp-form-input"
                  type="text"
                  value={assetPath}
                  onChange={(event) => {
                    clearErrors();
                    setAssetPath(event.target.value);
                  }}
                />
              </div>
            </>
          ) : null}
        </div>

        {validationErrors.form ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            {validationErrors.form}
          </p>
        ) : null}

        {mutation.state.status === "error" ? (
          <p className="cp-form-error cp-form-error--panel" role="alert">
            {mutationErrorMessage("save")}
          </p>
        ) : null}

        <div className="cp-form-actions cp-form-actions--sticky">
          <button
            type="button"
            className="cp-button cp-button--secondary"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="cp-button cp-button--primary"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? mode.kind === "create"
                ? "Creating…"
                : "Saving…"
              : mode.kind === "create"
                ? "Create Value"
                : "Save Changes"}
          </button>
        </div>
      </form>
    </section>
  );
}

export function LovCatalogDetailPage({
  clinicianCode,
  catalogKey,
}: {
  clinicianCode: string;
  catalogKey: string;
}) {
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getLovOptions(clinicianCode, catalogKey, signal),
    [catalogKey, clinicianCode],
  );
  const state = usePortalData(request);
  const refreshOptions = state.refresh;
  const [localOptions, setLocalOptions] = useState<LovOption[] | null>(null);
  const [formMode, setFormMode] = useState<LovOptionFormMode | null>(null);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "admin" &&
      data.patient === null &&
      data.catalogKey === catalogKey &&
      data.catalog.key === catalogKey,
  );
  const catalog = available ? data?.catalog : undefined;

  useEffect(() => {
    if (data?.options) setLocalOptions(data.options);
  }, [data]);

  useEffect(() => {
    setFormMode(null);
  }, [catalogKey]);

  const options = useMemo(
    () =>
      sortLovOptions(
        localOptions ?? data?.options ?? [],
        catalog?.orderingMode ?? "fixed",
      ),
    [catalog?.orderingMode, data?.options, localOptions],
  );

  const applySavedOption = useCallback(
    (savedOption: LovOption) => {
      setLocalOptions((current) => {
        const source = current ?? data?.options ?? [];
        const existingIndex = source.findIndex(
          (option) => option.id === savedOption.id,
        );
        if (existingIndex === -1) return [...source, savedOption];
        return source.map((option) =>
          option.id === savedOption.id ? savedOption : option,
        );
      });
    },
    [data?.options],
  );

  const handleFormSaved = useCallback(
    (savedOption: LovOption, shouldRefresh: boolean) => {
      setFormMode(null);
      if (shouldRefresh) {
        refreshOptions();
        return;
      }
      applySavedOption(savedOption);
    },
    [applySavedOption, refreshOptions],
  );

  const showSortOrder = catalog?.orderingMode === "fixed";

  const optionColumns = useMemo<readonly PortalTableColumn<LovOption>[]>(
    () => [
      {
        key: "actions",
        header: <ActionsColumnHeader />,
        headerLabel: "Actions",
        initialWidth: 60,
        minWidth: 60,
        resizable: false,
        cellClassName: "cp-table-cell--compact",
        render: (option) => (
          <LovOptionRowActions
            clinicianCode={clinicianCode}
            catalogKey={catalogKey}
            option={option}
            onEdit={(selectedOption) =>
              setFormMode({ kind: "edit", option: selectedOption })
            }
            onSaved={applySavedOption}
          />
        ),
      },
      {
        key: "name",
        header: "Name",
        initialWidth: 240,
        minWidth: 170,
        render: (option) => option.name,
      },
      {
        key: "value",
        header: "Value",
        initialWidth: 250,
        minWidth: 170,
        render: (option) => (
          <code className="cp-registry-code">{option.value}</code>
        ),
      },
      {
        key: "key",
        header: "Key",
        initialWidth: 220,
        minWidth: 160,
        render: (option) => (
          <code className="cp-registry-code">{option.key}</code>
        ),
      },
      {
        key: "color",
        header: "Color",
        initialWidth: 160,
        minWidth: 130,
        render: (option) => (
          <span
            className="cp-value-pill"
            data-palette={getActivityTypePillToken(
              option.value,
              option.colorKey,
            )}
          >
            {option.colorKey || "Not set"}
          </span>
        ),
      },
      {
        key: "status",
        header: "Status",
        initialWidth: 120,
        minWidth: 105,
        resizable: false,
        render: (option) => (
          <StatusPill status={option.enabled ? "Enabled" : "Disabled"} />
        ),
      },
      {
        key: "default",
        header: "Default",
        initialWidth: 115,
        minWidth: 105,
        resizable: false,
        render: (option) =>
          option.isDefault ? (
            <span className="cp-lov-default-pill">Default</span>
          ) : (
            <span className="cp-empty-value">—</span>
          ),
      },
      ...(showSortOrder
        ? [
            {
              key: "sortOrder",
              header: "Sort Order",
              initialWidth: 120,
              minWidth: 105,
              render: (option: LovOption) => option.sortOrder,
            },
          ]
        : []),
    ],
    [applySavedOption, catalogKey, clinicianCode, showSortOrder],
  );

  const nextSortOrder = options.length
    ? Math.max(...options.map((option) => option.sortOrder)) + 1
    : 0;

  return (
    <ClinicianPortalLayout
      activeGlobalKey="lists-of-values"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title={catalog?.name || "List of Values"}
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Administration" },
              {
                label: "Lists of Values",
                path: "/clinician-portal/admin/lists-of-values",
              },
              { label: catalog?.name || "List Details" },
            ]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide">
        {state.status === "loading" ? (
          <LoadingState label="Loading List of Values" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("List of Values is unavailable")} />
        ) : null}
        {available && data && catalog ? (
          <>
            <ContentSection title="Catalog Details">
              <RecordDetailCard>
                <RecordFieldRow label="Name">
                  <DisplayValue value={catalog.name} />
                </RecordFieldRow>
                <RecordFieldRow label="Description">
                  <span className="cp-multiline-value">
                    <DisplayValue value={catalog.description} />
                  </span>
                </RecordFieldRow>
                <RecordFieldRow label="Selection Type">
                  {formatLovSelectionType(catalog.selectionType)}
                </RecordFieldRow>
                <RecordFieldRow label="Ordering">
                  {formatLovOrderingMode(catalog.orderingMode)}
                </RecordFieldRow>
                <RecordFieldRow label="Status">
                  <StatusPill status={catalog.enabled ? "Active" : "Inactive"} />
                </RecordFieldRow>
              </RecordDetailCard>
            </ContentSection>

            {formMode ? (
              <ContentSection
                title={formMode.kind === "create" ? "Add Value" : "Edit Value"}
              >
                <LovOptionForm
                  key={
                    formMode.kind === "create"
                      ? `create-${formMode.sortOrder}`
                      : `edit-${formMode.option.id}`
                  }
                  clinicianCode={clinicianCode}
                  catalogKey={catalogKey}
                  selectionType={catalog.selectionType}
                  mode={formMode}
                  onCancel={() => setFormMode(null)}
                  onSaved={handleFormSaved}
                />
              </ContentSection>
            ) : null}

            <ContentSection
              title="Options"
              actions={
                <button
                  type="button"
                  className="cp-button cp-button--primary"
                  onClick={() =>
                    setFormMode({ kind: "create", sortOrder: nextSortOrder })
                  }
                >
                  + Add Value
                </button>
              }
            >
              {options.length ? (
                <PortalDataTable
                  caption={`${catalog.name} options`}
                  columns={optionColumns}
                  records={options}
                  getRecordKey={(option) => option.id}
                />
              ) : (
                <EmptyState
                  title="No options found."
                  description="This value catalog does not currently contain any options."
                />
              )}
            </ContentSection>
          </>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
